import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import adminStyles from "../../styles/components/AdminDetailModals.module.css";
import contactStyles from "../../styles/components/ContactDetailModal.module.css";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import { ReactComponent as GlassIcon } from "../../assets/icons/glass.svg";
import Avatar from "../../ui/avatar/Avatar";
import StatusBadge from "../../ui/tags/StatusBadge";
import DropdownField from "../../ui/editable/DropdownField";
import CompanyPicker from "./CompanyPicker";
import { contactsService } from "../../services/contacts.service";
import { useCompanyOptions } from "../../hooks/useCompanyOptions";
import { useOverlayClose } from "../../hooks/useOverlayClose";
import { genericService } from "../../services/generic.service";
import { validationService } from "../../services/validation.service";
import { useAuth } from "../../contexts/AuthContext";
import { getVerifiedStatusTooltip } from "../../utils/verificationLabels";
import {
  VERIFICATION_PENDING_MESSAGE,
  buildVerifyResultMessage,
  getVerifyErrorMessage,
  isVerificationPending,
} from "../../utils/verificationMessages";
import { measureTextWidth } from "../../utils/measureTextWidth";
import { VerifiedMark, NotDeliverableMark } from "./VerificationMarks";
import {
  ConfirmDeleteModal,
  ConfirmLoseValidationModal,
} from "./ContactConfirmModals";
import {
  toEmailObjects,
  toPhoneObjects,
  toCompanyValue,
  formatBackendErrors,
} from "../../utils/contactDetailHelpers";
import {
  getSelectableAccounts,
  resolveSelectableAccountId,
} from "../../utils/accountOptions";

const styles = { ...adminStyles, ...contactStyles };


const MODAL_BASE_WIDTH = 480;
const MODAL_MAX_WIDTH = 720;
const EMAIL_ROW_FIXED_WIDTH = 420;
const EMAIL_INPUT_FONT = "14px Urbanist, sans-serif";

function FieldRow({ label, children }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}

export default function ContactDetailModal({
  contact,
  onClose,
  onSaved,
  onDeleted,
  onOpenLead,
  onMerged,
  createMode = false,
  mergeMode = false,
  mergePersonIds = [],
}) {
  const { activeAccountId, accounts } = useAuth();
  const [isEditing, setIsEditing] = useState(createMode || mergeMode);
  const [accountId, setAccountId] = useState(null);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState(null);
  const [leads, setLeads] = useState(null);
  const leadsGenerationRef = useRef(0);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [verifyStateByEmail, setVerifyStateByEmail] = useState({});
  const [editVerifyingIndex, setEditVerifyingIndex] = useState(null);
  const [pendingValidationLoss, setPendingValidationLoss] = useState(null);
  const [invalidFields, setInvalidFields] = useState({
    firstName: false,
    lastName: false,
    emailTypeIndexes: [],
    phoneTypeIndexes: [],
  });

  const [firstName, setFirstName] = useState(contact?.first_name ?? "");
  const [lastName, setLastName] = useState(contact?.last_name ?? "");
  const [company, setCompany] = useState(() => toCompanyValue(contact));
  const [position, setPosition] = useState(contact?.position ?? "");
  const [emails, setEmails] = useState(() => toEmailObjects(contact));
  const [phones, setPhones] = useState(() => toPhoneObjects(contact));

  const [emailTypes, setEmailTypes] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);


  const accountOptions = useMemo(
    () => getSelectableAccounts(accounts),
    [accounts],
  );
  const effectiveAccountId = createMode
    ? accountId ??
      resolveSelectableAccountId(accounts, activeAccountId) ??
      activeAccountId ??
      accountOptions[0]?.id ??
      null
    : contact?.account ?? null;
  const selectedAccountOption =
    accountOptions.find(
      (account) => Number(account.id) === Number(effectiveAccountId),
    ) ?? null;

  const displayName =
    `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`.trim() ||
    "Nuevo Contacto";
  const subtitle = [contact?.company_name, contact?.position]
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    let cancelled = false;
    genericService
      .personFieldTypes()
      .then((data) => {
        if (cancelled) return;
        setEmailTypes(data?.email_types ?? []);
        setContactTypes(data?.contact_types ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setEmailTypes([]);
          setContactTypes([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const {
    options: companyOptions,
    loading: companyLoading,
    search: handleCompanySearch,
    loadMore: handleCompanyLoadMore,
    pin: pinCompany,
  } = useCompanyOptions({ accountId: effectiveAccountId });

  const selectedCompanyAccountRef = useRef(effectiveAccountId);

  useEffect(() => {
    if (!createMode) return;
    if (selectedCompanyAccountRef.current === effectiveAccountId) return;

    selectedCompanyAccountRef.current = effectiveAccountId;
    setCompany(null);
    setEmails((prev) =>
      prev.map((entry) =>
        entry.verificationPending ? { ...entry, verificationPending: false } : entry,
      ),
    );
  }, [createMode, effectiveAccountId]);

  useEffect(() => {
    if (!isEditing) return;
    handleCompanySearch("");
  }, [isEditing, handleCompanySearch]);

  const handleCompanyCreated = useCallback(
    (created) => {
      pinCompany(created);
    },
    [pinCompany],
  );

  const overlayCloseProps = useOverlayClose(onClose);

  useEffect(() => {
    leadsGenerationRef.current += 1;
    setLeads(null);
    setLeadsOpen(false);
    setLeadsLoading(false);
    setLeadsError(null);
  }, [contact?.id]);

  async function handleToggleLeads() {
    const willOpen = !leadsOpen;
    setLeadsOpen(willOpen);
    if (!willOpen || leads !== null || leadsLoading) return;
    const generation = leadsGenerationRef.current;
    setLeadsLoading(true);
    setLeadsError(null);
    try {
      const { results } = await contactsService.getLeads(contact.id);
      if (generation !== leadsGenerationRef.current) return;
      setLeads(results);
    } catch {
      if (generation === leadsGenerationRef.current) {
        setLeadsError("No se pudieron cargar los leads del contacto.");
      }
    } finally {
      if (generation === leadsGenerationRef.current) {
        setLeadsLoading(false);
      }
    }
  }

  function startEdit() {
    setFirstName(contact?.first_name ?? "");
    setLastName(contact?.last_name ?? "");
    setCompany(toCompanyValue(contact));
    setPosition(contact?.position ?? "");
    setEmails(toEmailObjects(contact));
    setPhones(toPhoneObjects(contact));
    setError(null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setError(null);
    if (createMode || mergeMode) {
      onClose();
    } else {
      setIsEditing(false);
    }
  }

  async function handleSave() {
    const activeEmails = emails.filter(
      (email) => email.active !== false && email.data.trim(),
    );
    const filledPhones = phones.filter((phone) => phone.data.trim());

    const emailTypeIndexes = emails
      .map((email, index) =>
        email.active !== false && email.data.trim() && !email.type ? index : null,
      )
      .filter((index) => index !== null);
    const phoneTypeIndexes = phones
      .map((phone, index) => (phone.data.trim() && !phone.type ? index : null))
      .filter((index) => index !== null);

    const nextInvalid = {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      emailTypeIndexes,
      phoneTypeIndexes,
    };
    setInvalidFields(nextInvalid);

    if (nextInvalid.firstName || nextInvalid.lastName) {
      setError("Completá los campos obligatorios marcados en rojo.");
      return;
    }
    if (emailTypeIndexes.length) {
      setError("Elegí un tipo para cada email.");
      return;
    }
    if (phoneTypeIndexes.length) {
      setError("Elegí un tipo para cada teléfono.");
      return;
    }

    const verificationTokens = activeEmails
      .map((email) => email.verification?.verification_token)
      .filter(Boolean);

    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      company: company?.id ?? null,
      position: position.trim(),
      ...(createMode && effectiveAccountId
        ? { account_id: effectiveAccountId }
        : {}),
      ...(verificationTokens.length ? { verification_tokens: verificationTokens } : {}),
      emails: [
        ...activeEmails.map((email) => ({
          ...(email.id ? { id: email.id } : {}),
          type: email.type,
          data: email.data.trim(),
          active: true,
        })),
        ...emails
          .filter((email) => email.active === false && email.id)
          .map((email) => ({ id: email.id, active: false })),
      ],
      contacts: filledPhones.map((phone) => ({
        ...(phone.id ? { id: phone.id } : {}),
        type: phone.type,
        data: phone.data.trim(),
      })),
    };

    setSaving(true);
    setError(null);
    try {
      let result;
      if (mergeMode) {
        result = await contactsService.merge(mergePersonIds, payload);
        onMerged?.(result);
        return;
      }
      if (createMode) {
        result = await contactsService.create(payload);
      } else {
        const updated = await contactsService.update(contact.id, payload);
        // Re-consultamos el detalle para reflejar las relaciones (emails, etc.)
        // recién persistidas, sin depender de la respuesta del PATCH.
        let refreshed = null;
        try {
          refreshed = await contactsService.getDetail(contact.id);
        } catch {
          refreshed = null;
        }
        result = refreshed ?? updated;
      }
      onSaved?.(result);
      if (!createMode) setIsEditing(false);
    } catch (err) {
      console.error(err);
      const responseData = err?.response?.data;
      setError(
        formatBackendErrors(responseData?.errors) ||
          responseData?.message ||
          "No se pudo guardar el contacto.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleVerifyEmail(emailEntry) {
    const emailAddress = emailEntry.data;

    setVerifyStateByEmail((prev) => ({
      ...prev,
      [emailAddress]: { verifying: true, error: null, result: null, pending: false },
    }));
    try {
      const result = await validationService.verifySingle({
        accountId: contact?.account ?? activeAccountId,
        personId: contact.id,
        email: emailAddress,
      });
      if (isVerificationPending(result)) {
        setVerifyStateByEmail((prev) => ({
          ...prev,
          [emailAddress]: { verifying: false, error: null, result: null, pending: true },
        }));
        return;
      }
      setVerifyStateByEmail((prev) => ({
        ...prev,
        [emailAddress]: { verifying: false, error: null, result },
      }));
      try {
        const refreshed = await contactsService.getDetail(contact.id);
        if (refreshed) onSaved?.(refreshed);
      } catch {
        onSaved?.({
          ...contact,
          is_verified: !!result.is_verified,
          verified_at:
            result.verified_at ??
            (result.is_verified
              ? new Date().toISOString()
              : contact.verified_at),
        });
      }
    } catch (err) {
      setVerifyStateByEmail((prev) => ({
        ...prev,
        [emailAddress]: {
          verifying: false,
          error: getVerifyErrorMessage(err),
          result: null,
        },
      }));
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await contactsService.remove(contact.id);
      onDeleted?.(contact.id);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  }

  function updateEmail(emailIndex, changes) {
    setEmails((prev) =>
      prev.map((email, index) => (index === emailIndex ? { ...email, ...changes } : email)),
    );
  }

  function handleEmailDataChange(emailIndex, newValue) {
    const entry = emails[emailIndex];
    if (entry?.verification?.is_verified && newValue !== entry.data) {
      setPendingValidationLoss({
        kind: "edit",
        emailIndex,
        newValue,
        emailAddress: entry.data,
      });
      return;
    }
    updateEmail(emailIndex, {
      data: newValue,
      verification: null,
      verificationError: null,
      verificationPending: false,
    });
  }

  async function handleVerifyTypedEmail(emailIndex) {
    const emailAddress = emails[emailIndex]?.data.trim();
    if (!emailAddress || editVerifyingIndex !== null) return;
    setEditVerifyingIndex(emailIndex);
    try {
      const result = await validationService.verifySingle({
        accountId: effectiveAccountId,
        email: emailAddress,
      });
      const verificationPending = isVerificationPending(result);
      setEmails((prev) =>
        prev.map((entry, index) =>
          index === emailIndex && entry.data.trim() === emailAddress
            ? {
                ...entry,
                verification: verificationPending ? null : result,
                verificationError: null,
                verificationPending,
              }
            : entry,
        ),
      );
    } catch (err) {
      const errorMessage = getVerifyErrorMessage(err);
      setEmails((prev) =>
        prev.map((entry, index) =>
          index === emailIndex && entry.data.trim() === emailAddress
            ? {
                ...entry,
                verification: null,
                verificationError: errorMessage,
                verificationPending: false,
              }
            : entry,
        ),
      );
    } finally {
      setEditVerifyingIndex(null);
    }
  }

  function doRemoveEmail(emailIndex) {
    setEmails((prev) => {
      const entryToRemove = prev[emailIndex];
      if (entryToRemove.id) {
        return prev.map((email, index) =>
          index === emailIndex ? { ...email, active: false } : email,
        );
      }
      return prev.filter((_, index) => index !== emailIndex);
    });
  }

  function removeEmail(emailIndex) {
    const entry = emails[emailIndex];
    if (entry?.verification?.is_verified) {
      setPendingValidationLoss({
        kind: "remove",
        emailIndex,
        emailAddress: entry.data,
      });
      return;
    }
    doRemoveEmail(emailIndex);
  }

  function confirmPendingValidationLoss() {
    const pendingAction = pendingValidationLoss;
    if (!pendingAction) return;
    setPendingValidationLoss(null);
    if (pendingAction.kind === "edit") {
      updateEmail(pendingAction.emailIndex, {
        data: pendingAction.newValue,
        verification: null,
        verificationError: null,
        verificationPending: false,
      });
    } else {
      doRemoveEmail(pendingAction.emailIndex);
    }
  }
  function addEmail() {
    setEmails((prev) => [...prev, { data: "", type: null }]);
  }
  function updatePhone(phoneIndex, changes) {
    setPhones((prev) =>
      prev.map((phone, index) => (index === phoneIndex ? { ...phone, ...changes } : phone)),
    );
  }
  function removePhone(phoneIndex) {
    setPhones((prev) => {
      const entry = prev[phoneIndex];
      if (entry.id) return prev;
      return prev.filter((_, index) => index !== phoneIndex);
    });
  }
  function addPhone() {
    setPhones((prev) => [...prev, { data: "", type: null }]);
  }

  const visibleEmails = emails.filter((email) => email.active !== false);
  const activeContactEmails =
    contact?.emails?.filter((email) => email.active !== false && email.data) ??
    [];
  const isVerified = !!contact?.is_verified;
  const primaryPhone = contact?.contacts?.[0]?.data ?? "-";

  const anyEmailVerifying = Object.values(verifyStateByEmail).some(
    (verifyState) => verifyState.verifying,
  );

  const modalWidth = useMemo(() => {
    if (!isEditing) return MODAL_BASE_WIDTH;
    const widestEmailPx = Math.max(
      measureTextWidth("email@dominio.com", EMAIL_INPUT_FONT),
      ...emails
        .filter((email) => email.active !== false)
        .map((email) => measureTextWidth(email.data, EMAIL_INPUT_FONT)),
    );
    return Math.min(
      MODAL_MAX_WIDTH,
      Math.max(MODAL_BASE_WIDTH, Math.ceil(widestEmailPx) + EMAIL_ROW_FIXED_WIDTH),
    );
  }, [isEditing, emails]);

  const usedEmailTypeIds = emails
    .filter((email) => email.active !== false && email.type)
    .map((email) => Number(email.type));
  const usedPhoneTypeIds = phones
    .filter((phone) => phone.type)
    .map((phone) => Number(phone.type));

  function renderTypeSelect(typeOptions, currentType, usedTypeIds, onTypeChange, invalid) {
    return (
      <select
        className={`${styles.typeSelect} ${invalid ? styles.fieldInputError : ""}`}
        aria-invalid={invalid || undefined}
        value={currentType ?? ""}
        onChange={(event) =>
          onTypeChange(event.target.value ? Number(event.target.value) : null)
        }
        disabled={!typeOptions.length}
      >
        <option value="">Tipo</option>
        {typeOptions.map((typeOption) => {
          const isUsedElsewhere =
            usedTypeIds.includes(Number(typeOption.id)) &&
            Number(typeOption.id) !== Number(currentType);
          return (
            <option key={typeOption.id} value={typeOption.id} disabled={isUsedElsewhere}>
              {typeOption.name}
            </option>
          );
        })}
      </select>
    );
  }

  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div
        className={styles.modal}
        style={{ width: modalWidth }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerAvatar}>
            <Avatar
              src={contact?.photo_url ?? null}
              name={displayName}
              size={56}
            />
          </div>

          <div className={styles.headerInfo}>
            <div className={styles.headerNameRow}>
              <span className={styles.modalName}>{displayName}</span>
              <StatusBadge
                isActive={isVerified}
                size={20}
                title={getVerifiedStatusTooltip(contact)}
              />
            </div>
            {subtitle ? (
              <span className={styles.modalSubtitle}>{subtitle}</span>
            ) : null}
          </div>

          <div className={styles.headerActions}>
            {isEditing && !createMode && !mergeMode && (
              <button
                className={styles.trashBtn}
                onClick={() => setShowDelete(true)}
                aria-label="Eliminar contacto"
              >
                <TrashIcon className={styles.trashIcon} />
              </button>
            )}
          </div>
        </div>

        {mergeMode && (
          <div className={styles.mergeHint}>
            Así quedará el contacto luego de unificar{" "}
            {mergePersonIds.length} contactos. Revisá y ajustá los datos antes
            de confirmar; los demás contactos quedarán ocultos.
          </div>
        )}

        {/* Fields */}
        <div className={styles.modalFields}>
          {(createMode
            ? accountOptions.length > 1
            : Boolean(contact?.account_name)) && (
            <FieldRow label="Cuenta">
              {createMode ? (
                <DropdownField
                  className={styles.accountDropdown}
                  items={accountOptions}
                  value={selectedAccountOption}
                  labelKey="name"
                  placeholder="Seleccionar cuenta"
                  onChange={(account) => setAccountId(account?.id ?? null)}
                />
              ) : (
                <span className={styles.fieldText}>
                  {contact?.account_name || "-"}
                </span>
              )}
            </FieldRow>
          )}

          <FieldRow label="Nombre">
            {isEditing ? (
              <input
                className={`${styles.fieldInput} ${invalidFields.firstName ? styles.fieldInputError : ""}`}
                aria-invalid={invalidFields.firstName || undefined}
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                  if (invalidFields.firstName) {
                    setInvalidFields((prev) => ({ ...prev, firstName: false }));
                  }
                }}
                placeholder="Nombre"
              />
            ) : (
              <span className={styles.fieldText}>
                {contact?.first_name || "-"}
              </span>
            )}
          </FieldRow>

          <FieldRow label="Apellido">
            {isEditing ? (
              <input
                className={`${styles.fieldInput} ${invalidFields.lastName ? styles.fieldInputError : ""}`}
                aria-invalid={invalidFields.lastName || undefined}
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                  if (invalidFields.lastName) {
                    setInvalidFields((prev) => ({ ...prev, lastName: false }));
                  }
                }}
                placeholder="Apellido"
              />
            ) : (
              <span className={styles.fieldText}>
                {contact?.last_name || "-"}
              </span>
            )}
          </FieldRow>

          <FieldRow label="Empresa">
            {isEditing ? (
              <CompanyPicker
                className={styles.companyDropdown}
                items={companyOptions}
                value={company}
                loading={companyLoading}
                accountId={effectiveAccountId}
                onSearch={handleCompanySearch}
                onLoadMore={handleCompanyLoadMore}
                onChange={setCompany}
                onCreated={handleCompanyCreated}
              />
            ) : (
              <span className={styles.fieldText}>
                {contact?.company_name || "-"}
              </span>
            )}
          </FieldRow>

          <FieldRow label="Posición">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={position}
                onChange={(event) => setPosition(event.target.value)}
                placeholder="Posición / Cargo"
              />
            ) : (
              <span className={styles.fieldText}>
                {contact?.position || "-"}
              </span>
            )}
          </FieldRow>

          <FieldRow label="Email">
            {isEditing ? (
              <div className={styles.fieldEmailList}>
                {visibleEmails.map((emailEntry) => {
                  const emailIndex = emails.indexOf(emailEntry);
                  const trimmedEmail = emailEntry.data.trim();
                  const isVerifyingThisEmail = editVerifyingIndex === emailIndex;
                  return (
                    <div key={emailIndex} className={styles.emailVerifyBlock}>
                      <div className={styles.fieldEmailItem}>
                        {renderTypeSelect(
                          emailTypes,
                          emailEntry.type,
                          usedEmailTypeIds,
                          (newType) => {
                            updateEmail(emailIndex, { type: newType });
                            setInvalidFields((prev) => ({
                              ...prev,
                              emailTypeIndexes: prev.emailTypeIndexes.filter(
                                (index) => index !== emailIndex,
                              ),
                            }));
                          },
                          invalidFields.emailTypeIndexes.includes(emailIndex),
                        )}
                        <input
                          className={`${styles.fieldInput} ${styles.fieldInputFlex}`}
                          value={emailEntry.data}
                          onChange={(event) =>
                            handleEmailDataChange(emailIndex, event.target.value)
                          }
                          placeholder="email@dominio.com"
                          type="email"
                        />
                        {trimmedEmail.includes("@") &&
                          !emailEntry.verification &&
                          !emailEntry.verificationPending && (
                          <button
                            type="button"
                            className={styles.verifyIconBtn}
                            onClick={() => handleVerifyTypedEmail(emailIndex)}
                            disabled={editVerifyingIndex !== null}
                            aria-label={`Verificar ${trimmedEmail}`}
                            title="Verificar email (2 créditos de validación)"
                          >
                            {isVerifyingThisEmail ? (
                              <span className={styles.verifySpinner} />
                            ) : (
                              <GlassIcon className={styles.verifyGlassIcon} />
                            )}
                          </button>
                        )}
                        {emailEntry.verification?.is_verified && (
                          <VerifiedMark emailAddress={trimmedEmail} />
                        )}
                        {emailEntry.verification &&
                          !emailEntry.verification.is_verified && (
                            <NotDeliverableMark emailAddress={trimmedEmail} />
                          )}
                        {(visibleEmails.length > 1 || !emailEntry.id) && (
                          <button
                            className={styles.removeItemBtn}
                            onClick={() => removeEmail(emailIndex)}
                            disabled={!!emailEntry.id && visibleEmails.length <= 1}
                          >
                            <CrossIcon className={styles.removeItemIcon} />
                          </button>
                        )}
                      </div>
                      {isVerifyingThisEmail && (
                        <div className={styles.verifyHint}>
                          Verificando la entregabilidad del email. Esto puede
                          tardar hasta 40 segundos…
                        </div>
                      )}
                      {!isVerifyingThisEmail && emailEntry.verificationPending && (
                        <div className={styles.verifyHint}>
                          {VERIFICATION_PENDING_MESSAGE}
                        </div>
                      )}
                      {!isVerifyingThisEmail && emailEntry.verificationError && (
                        <div className={styles.verifyError}>
                          {emailEntry.verificationError}
                        </div>
                      )}
                      {!isVerifyingThisEmail && emailEntry.verification && (
                        <div className={styles.verifyResult}>
                          {buildVerifyResultMessage(emailEntry.verification)}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button className={styles.addItemBtn} onClick={addEmail}>
                  <AddIcon style={{ width: 12, height: 12 }} />
                  Agregar email
                </button>
                <div className={styles.verifyHint}>
                  Verificar un email cuesta 2 créditos de validación.
                </div>
              </div>
            ) : (
              <div className={styles.emailVerifyList}>
                {activeContactEmails.length === 0 && (
                  <span className={styles.fieldText}>-</span>
                )}
                {activeContactEmails.map((emailEntry) => {
                  const verifyState =
                    verifyStateByEmail[emailEntry.data] ?? {};
                  return (
                    <div
                      key={emailEntry.id ?? emailEntry.data}
                      className={styles.emailVerifyBlock}
                    >
                      <div className={styles.emailVerifyRow}>
                        <span
                          className={`${styles.fieldText} ${styles.emailVerifyAddress}`}
                        >
                          {emailEntry.data}
                        </span>
                        {!createMode && !verifyState.result && !verifyState.pending && (
                          <button
                            type="button"
                            className={styles.verifyIconBtn}
                            onClick={() => handleVerifyEmail(emailEntry)}
                            disabled={anyEmailVerifying}
                            aria-label={`Verificar ${emailEntry.data}`}
                            title="Verificar email (2 créditos de validación)"
                          >
                            {verifyState.verifying ? (
                              <span className={styles.verifySpinner} />
                            ) : (
                              <GlassIcon className={styles.verifyGlassIcon} />
                            )}
                          </button>
                        )}
                        {verifyState.result?.is_verified && (
                          <VerifiedMark emailAddress={emailEntry.data} />
                        )}
                        {verifyState.result &&
                          !verifyState.result.is_verified && (
                            <NotDeliverableMark emailAddress={emailEntry.data} />
                          )}
                      </div>
                      {verifyState.verifying && (
                        <div className={styles.verifyHint}>
                          Verificando la entregabilidad del email. Esto puede
                          tardar hasta 40 segundos…
                        </div>
                      )}
                      {verifyState.pending && (
                        <div className={styles.verifyHint}>
                          {VERIFICATION_PENDING_MESSAGE}
                        </div>
                      )}
                      {!verifyState.verifying && verifyState.error && (
                        <div className={styles.verifyError}>
                          {verifyState.error}
                        </div>
                      )}
                      {!verifyState.verifying && verifyState.result && (
                        <div className={styles.verifyResult}>
                          {buildVerifyResultMessage(verifyState.result)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </FieldRow>

          <FieldRow label="Celular">
            {isEditing ? (
              <div className={styles.fieldEmailList}>
                {phones.map((phoneEntry, phoneIndex) => (
                  <div key={phoneIndex} className={styles.fieldEmailItem}>
                    {renderTypeSelect(
                      contactTypes,
                      phoneEntry.type,
                      usedPhoneTypeIds,
                      (newType) => {
                        updatePhone(phoneIndex, { type: newType });
                        setInvalidFields((prev) => ({
                          ...prev,
                          phoneTypeIndexes: prev.phoneTypeIndexes.filter(
                            (index) => index !== phoneIndex,
                          ),
                        }));
                      },
                      invalidFields.phoneTypeIndexes.includes(phoneIndex),
                    )}
                    <input
                      className={`${styles.fieldInput} ${styles.fieldInputFlex}`}
                      value={phoneEntry.data}
                      onChange={(event) =>
                        updatePhone(phoneIndex, { data: event.target.value })
                      }
                      placeholder="+54 11 1234 5678"
                      type="tel"
                    />
                    {!phoneEntry.id && phones.length > 1 && (
                      <button
                        className={styles.removeItemBtn}
                        onClick={() => removePhone(phoneIndex)}
                      >
                        <CrossIcon className={styles.removeItemIcon} />
                      </button>
                    )}
                  </div>
                ))}
                <button className={styles.addItemBtn} onClick={addPhone}>
                  <AddIcon style={{ width: 12, height: 12 }} />
                  Agregar teléfono
                </button>
              </div>
            ) : (
              <span className={styles.fieldText}>{primaryPhone}</span>
            )}
          </FieldRow>

        </div>

        {!createMode && contact && !isEditing && (
          <div className={styles.leadsSection}>
            <button
              type="button"
              className={styles.leadsToggleBtn}
              onClick={handleToggleLeads}
            >
              {leadsOpen ? "Ocultar leads asociados" : "Ver leads asociados"}
            </button>

            {leadsOpen && (
              <>
                {leadsLoading && (
                  <div className={styles.leadsHint}>Cargando leads…</div>
                )}

                {!leadsLoading && leadsError && (
                  <div className={styles.leadsError}>{leadsError}</div>
                )}

                {!leadsLoading && !leadsError && leads && leads.length === 0 && (
                  <div className={styles.leadsHint}>
                    Este contacto no está asociado a ningún lead.
                  </div>
                )}

                {!leadsLoading && !leadsError && leads && leads.length > 0 && (
                  <>
                    <div className={styles.leadsCount}>
                      {leads.length === 1
                        ? "Este contacto está asociado a 1 lead."
                        : `Este contacto está asociado a ${leads.length} leads.`}
                    </div>
                    <div className={styles.leadsTableWrap}>
                      <div className={styles.leadsTableHeader}>
                        <span>Lead</span>
                        <span>Estado</span>
                      </div>
                      {leads.map((lead) => (
                        <button
                          type="button"
                          key={lead.id}
                          className={`${styles.leadRow} ${lead.has_access ? "" : styles.leadRowDisabled}`}
                          aria-disabled={!lead.has_access || undefined}
                          onClick={() => {
                            if (lead.has_access) onOpenLead?.(lead.id);
                          }}
                          title={
                            lead.has_access
                              ? "Abrir lead"
                              : "No tenés acceso a este lead"
                          }
                        >
                          <span className={styles.leadCell}>
                            {lead.lead_name || "-"}
                          </span>
                          <span className={styles.leadCell}>
                            {lead.status_name || "-"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={styles.modalFooter}>
          {error && <div className={styles.fieldError}>{error}</div>}

          {isEditing ? (
            <div className={styles.footerRow}>
              <button className={styles.cancelBtn} onClick={cancelEdit}>
                Cancelar
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? mergeMode
                    ? "Unificando..."
                    : "Guardando..."
                  : mergeMode
                    ? "Unificar contactos"
                    : createMode
                      ? "Crear contacto"
                      : "Guardar cambios"}
              </button>
            </div>
          ) : (
            <button className={styles.editBtn} onClick={startEdit} type="button">
              <EditIcon className={styles.editIcon} />
              Editar información
            </button>
          )}
        </div>

      </div>

      {showDelete && (
        <ConfirmDeleteModal
          name={displayName}
          onCancel={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {pendingValidationLoss && (
        <ConfirmLoseValidationModal
          actionKind={pendingValidationLoss.kind}
          emailAddress={pendingValidationLoss.emailAddress}
          onCancel={() => setPendingValidationLoss(null)}
          onConfirm={confirmPendingValidationLoss}
        />
      )}
    </div>
  );
}
